import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "@/features/auth/AuthContext";
import { lerPreferencia, salvarPreferencia } from "@/features/preferences/api";
import { applyWidgetWindow } from "./chartWidgets";
import type { ProductionHistoryWindow } from "@/features/devices/types";
import type { DashboardWidget } from "./types";

/**
 * A janela do grafico vira escolha PESSOAL.
 *
 * O defeito que isto corrige: trocar 24 h por 7 d gravava no layout do painel,
 * que e compartilhado. Uma pessoa conferindo o historico da semana mudava o que
 * o turno inteiro via — e ninguem entendia por que o painel "mudou sozinho".
 *
 * O modelo e de LENTE, nao de copia. O layout salvo continua sendo o padrao que
 * o admin definiu; a preferencia se sobrepoe a ele na leitura. Apagar a
 * preferencia devolve a pessoa ao padrao — inclusive se o admin mudar esse
 * padrao depois, o que uma copia por usuario nao conseguiria acompanhar.
 *
 * Sem identidade (bancada) o hook se declara inativo e quem chama volta a
 * gravar no layout. La ha uma pessoa so, entao nao existe estado coletivo para
 * atropelar.
 */
export interface WidgetWindows {
  /** Leitura pessoal confirmada e sem escolha pendente; bancada nao exige GET. */
  preferencesConfirmed: boolean;
  /** `false` = sem identidade; quem chama deve gravar no layout compartilhado. */
  pessoal: boolean;
  /** Sobrepoe as janelas da pessoa aos widgets salvos. */
  aplicar: (widgets: DashboardWidget[]) => DashboardWidget[];
  /** `true` se a escolha foi absorvida aqui. */
  definir: (widgetId: string, janela: ProductionHistoryWindow) => boolean;
  error: string | null;
}

type Mapa = Record<string, ProductionHistoryWindow>;
const EMPTY_WINDOWS: Mapa = {};
// Fila compartilhada entre montagens: A -> B -> A nao deixa o GET novo
// ultrapassar uma escolha de A que ainda estava sendo gravada.
const pendingPreferences = new Map<string, Promise<void>>();
function enqueuePreference(key: string, task: () => Promise<void>): void {
  const pending = (pendingPreferences.get(key) ?? Promise.resolve()).then(task);
  pendingPreferences.set(key, pending);
  void pending.finally(() => {
    if (pendingPreferences.get(key) === pending) pendingPreferences.delete(key);
  });
}

export function useWidgetWindows(
  workspaceId: string | null,
  machineId: string | null,
): WidgetWindows {
  const { usuario, isCurrentUser } = useAuth();
  const pessoal = usuario !== null && workspaceId !== null && machineId !== null;
  const chave = pessoal ? `janela-grafico:${workspaceId}:${machineId}` : null;

  const scope = useMemo(() => ({
    key: chave, userId: usuario?.id, active: false, epoch: 0, revision: 0,
    confirmed: {} as Mapa, edits: {} as Mapa, readOk: false,
    queueKey: JSON.stringify([usuario?.id, chave]),
  }), [chave, usuario?.id]);
  const [view, setView] = useState({ scope, values: {} as Mapa, error: null as string | null });
  const janelas = view.scope === scope ? view.values : EMPTY_WINDOWS;

  useEffect(() => {
    if (!scope.key) return;
    scope.active = true;
    const epoch = ++scope.epoch;
    const controller = new AbortController();
    enqueuePreference(scope.queueKey, async () => {
      try {
        if (!isCurrentUser(scope.userId) || controller.signal.aborted) return;
        const lido = await lerPreferencia<Mapa>(scope.key!, controller.signal);
        if (!isCurrentUser(scope.userId) || scope.epoch !== epoch) return;
        scope.confirmed = lido ?? {};
        scope.readOk = true;
        if (scope.active) setView({ scope, values: { ...scope.confirmed, ...scope.edits }, error: null });
      } catch {
        if (!scope.active || scope.epoch !== epoch) return;
        setView({ scope, values: scope.edits, error: "Não foi possível carregar suas preferências de gráfico." });
      }
    });
    return () => {
      scope.active = false;
      // Uma escolha explicita pendente precisa do GET para preservar outros
      // cards. Conclui ao sair da rota, mas a guarda de sessao bloqueia logout.
      if (scope.revision === 0) controller.abort();
    };
  }, [scope, isCurrentUser]);

  const aplicar = useCallback(
    (widgets: DashboardWidget[]) => {
      if (!pessoal) return widgets;
      let mudou = false;
      const resultado = widgets.map((widget) => {
        const janela = janelas[widget.id];
        if (!janela) return widget;
        const ajustado = applyWidgetWindow(widget, janela);
        if (ajustado !== widget) mudou = true;
        return ajustado;
      });
      // Devolve o array original quando nada mudou: o painel memoiza por
      // referencia, e um array novo a cada render refaria trabalho a toa.
      return mudou ? resultado : widgets;
    },
    [pessoal, janelas],
  );

  const definir = useCallback(
    (widgetId: string, janela: ProductionHistoryWindow) => {
      if (!scope.key || !scope.active) return false;
      const revision = ++scope.revision;
      const edits = { ...scope.edits, [widgetId]: janela };
      scope.edits = edits;
      setView({ scope, values: { ...scope.confirmed, ...edits }, error: null });
      // Espera o GET inicial e serializa writes: resposta antiga nao pode
      // vencer a escolha mais nova nem apagar preferencias de outros cards.
      enqueuePreference(scope.queueKey, async () => {
        if (!isCurrentUser(scope.userId)) return;
        const proximo = { ...scope.confirmed, ...edits };
        try {
          if (!scope.readOk) throw new Error("Preferencia inicial nao confirmada");
          await salvarPreferencia(scope.key!, proximo);
          scope.confirmed = proximo;
          if (scope.active && revision === scope.revision) {
            scope.edits = {};
            setView({ scope, values: proximo, error: null });
          }
        } catch {
          if (!scope.active || revision !== scope.revision) return;
          scope.edits = {};
          setView({ scope, values: scope.confirmed,
            error: "Não foi possível salvar o período. A última preferência confirmada foi restaurada." });
        }
      });
      return true;
    },
    [scope, isCurrentUser],
  );

  return { pessoal, aplicar, definir, error: view.scope === scope ? view.error : null,
    preferencesConfirmed: !pessoal || (view.scope === scope && scope.readOk &&
      view.error === null && Object.keys(scope.edits).length === 0) };
}
